import type Stripe from 'stripe-latest';
import { QUALITY, SECURITY } from '../featureFlags/flagNames';
import {
  ENTERPRISE_COMPLIANCE,
  ENTERPRISE_PLATFORM,
  ENTERPRISE_RELEASE_MANAGEMENT,
  fossaPlanMap,
  fossaPlanNames,
  FREE_PLAN_NAME,
  LEGACY_PREMIUM_PLAN_1,
  LEGACY_PREMIUM_PLAN_2,
  MAXIMUM_BUSINESS_SEATS,
  MINIMUM_BUSINESS_SEATS,
  MINIMUM_BUSINESS_SEATS_STEP,
  PREMIUM_COMP_SEC_PLAN_NAME,
  PREMIUM_COMPLIANCE_PLAN_NAME,
  PREMIUM_PER_REVISION_PLAN_NAME,
  STRIPE_PRICE_PREFIX,
} from './consts';
import { TIER_FEATURE_MAP } from './tierFeatures';
import type { FossaPlanName, FossaSubscriptionWithPlanName, StaticPlanName } from './consts';
import type { FlagName } from '../featureFlags/types';
import type { StripePriceLimit } from '../model-typings/stripepricelimits';

/**
 * Parameters to be used when fetching plan info for an org.
 */
type FetchPlanInfo = {
  /**
   * The fossa plan name to be checked if any.
   */
  plan?: FossaPlanName;
  /**
   * The Stripe price limit to check for if any.
   */
  stripePriceLimit?: StripePriceLimit | null;
};

export const isNonLegacyPlan = (plan: FossaPlanName) =>
  [
    FREE_PLAN_NAME,
    PREMIUM_COMPLIANCE_PLAN_NAME,
    PREMIUM_COMP_SEC_PLAN_NAME,
    PREMIUM_PER_REVISION_PLAN_NAME,
    ENTERPRISE_COMPLIANCE,
    ENTERPRISE_PLATFORM,
    ENTERPRISE_RELEASE_MANAGEMENT,
  ].includes(plan);

export const isDynamicPlan = (plan: string | null | undefined): boolean =>
  (plan && plan.startsWith(STRIPE_PRICE_PREFIX)) === true;

/**
 * given a stripe plan, returns subscription details
 */
export function getSubscription({
  plan = FREE_PLAN_NAME,
  stripePriceLimit,
}: FetchPlanInfo): FossaSubscriptionWithPlanName {
  const freePlanInfo = fossaPlanMap[FREE_PLAN_NAME];

  if (isDynamicPlan(plan)) {
    return {
      label: 'Dynamic Plan',
      description: 'Stripe dynamic plan. Information is managed in Stripe.',
      planName: plan,
      privateRepos: stripePriceLimit?.limits.projectCount || freePlanInfo.privateRepos,
    } as FossaSubscriptionWithPlanName;
  }

  if (!fossaPlanNames.includes(plan as StaticPlanName)) {
    throw new Error('FOSSA subscription plan not found');
  }

  return {
    ...fossaPlanMap[plan],
    planName: plan,
  } as FossaSubscriptionWithPlanName;
}

/**
 * Retrieves the price of FOSSA plan based on plan name. This is only used in the internal tools and admin pages.
 * Return $0 here is ok for dynamic plans for now since they'll not be managed by internal tools.
 */
export const getSubscriptionPrice = (stripePlan: FossaPlanName = FREE_PLAN_NAME): number => {
  const mappedPlanInfo = fossaPlanMap[stripePlan];
  if (!mappedPlanInfo) {
    return 0;
  }

  return mappedPlanInfo.price;
};

const enterprisePlans = new Set([ENTERPRISE_PLATFORM, ENTERPRISE_COMPLIANCE, ENTERPRISE_RELEASE_MANAGEMENT]);

/**
 * Helper function to see if an organization's is an enterprise plan. This can use one of the old static plan names
 * or the new dynamic price from Stripe. Both parameters are optional.
 *
 * @param params Parameters to use to check for enterprise tier.
 * @param {FossaPlanName} params.plan - The fossa plan name to be checked (optional)
 * @param {StripePriceLimit} params.stripePriceLimit - The Stripe price limit to check for (optional)
 */
export const isEnterpriseTier = (params: FetchPlanInfo | undefined | null): boolean => {
  if (!params || !params.plan) {
    return false;
  }

  if (params.plan && enterprisePlans.has(params.plan)) {
    return true;
  }

  if (params.stripePriceLimit && params.plan === params.stripePriceLimit.priceId) {
    return params.stripePriceLimit.type === 'ENTERPRISE';
  }

  return false;
};

const businessPlans = new Set([
  PREMIUM_COMPLIANCE_PLAN_NAME,
  PREMIUM_COMP_SEC_PLAN_NAME,
  PREMIUM_PER_REVISION_PLAN_NAME,
  LEGACY_PREMIUM_PLAN_1,
  LEGACY_PREMIUM_PLAN_2,
]);

/**
 * Helper function to see if an organization's `stripe_plan` is a business plan.
 * This can use one of the static business plan names
 * or the new dynamic price from Stripe. Both parameters are optional.
 *
 * @param {object} params - params object
 * @param {FossaPlanName} params.plan - The fossa plan name to be checked (optional)
 * @param {StripePriceLimit} params.stripePriceLimit - The Stripe price limit to check for (optional)
 */
export const isBusinessTier = (params: FetchPlanInfo | undefined | null): boolean => {
  if (!params || !params.plan) {
    return false;
  }

  if (businessPlans.has(params.plan)) {
    return true;
  }

  if (params.stripePriceLimit && params.plan === params.stripePriceLimit.priceId) {
    return params.stripePriceLimit.type === 'BUSINESS';
  }

  return false;
};

/**
 * Helper function to get the features included in a given tier.
 */
export const getFeaturesForTier = (plan: FossaPlanName | undefined): Set<FlagName> =>
  (plan ? TIER_FEATURE_MAP[plan] : new Set<FlagName>()) ?? new Set<FlagName>();

/**
 * Helper function to see if a feature is enabled for a given tier.
 */
export const hasTierFeature = (plan: FossaPlanName | undefined, flag: FlagName): boolean =>
  getFeaturesForTier(plan)
    .has(flag);

/**
 * Helper function to see if a plan is the free tier.
 * This can use the static free plan name
 * or the new dynamic price from Stripe. Both parameters are optional.
 *
 * @param {object|null} params - params object
 * @param {FossaPlanName} params.plan - The fossa plan name to be checked (optional)
 * @param {StripePriceLimit} params.stripePriceLimit - The Stripe price limit to check for (optional)
 */
export const isFreeTier = (params: FetchPlanInfo | undefined | null): boolean => {
  if (!params) {
    return false;
  }

  if (!params.plan) {
    return false;
  }

  if (params.plan === FREE_PLAN_NAME) {
    return true;
  }

  if (params.stripePriceLimit && params.plan === params.stripePriceLimit.priceId) {
    return params.stripePriceLimit.type === 'FREE';
  }
  return false;
};

/**
 * Determines whether or not the org should have quality from its tier. This is needed
 * for deciding whether the org should flip its quality flag or not.
 *
 * @param oldStripePlan - The name of the org's current Stripe plan.
 * @param newStripePlan - The name of the Stripe plan the org is changing to.
 * @returns False if the org isn't currently on a Quality enabled plan but has quality enabled (was manually enabled by an admin).
 *          False if the original tier and the new tier both have Quality.
 *          True if the org is moving to or from a tier that has quality.
 */
export const isQualityPlanChange = (
  oldStripePlan: FossaPlanName,
  newStripePlan: FossaPlanName,
  doesOrgHaveQuality: boolean = false,
): boolean => {
  // If the org was on a plan that didn't have quality by default, but does have quality enabled, then it was set manually
  // by an admin and we should persist that setting, so we return false to not flip the flag.
  if (doesOrgHaveQuality && !hasTierFeature(oldStripePlan, QUALITY)) {
    return false;
  }

  return hasTierFeature(newStripePlan, QUALITY) !== hasTierFeature(oldStripePlan, QUALITY);
};

/**
 * Helper function to see if a plan has security. This is checked often enough that it warrants its own helper.
 */
export const isSecurityPlan = (plan: FossaPlanName | undefined) => hasTierFeature(plan, SECURITY);

/**
 * Helper function to get all Security Plans dynamically so we can avoid hardcoding them.
 */
export const getSecurityPlans = (): FossaPlanName[] =>
  Object.keys(fossaPlanMap)
    .filter((plan) => isSecurityPlan(plan as FossaPlanName)) as FossaPlanName[];

/**
 * Determines whether or not we're upgrading to/downgrading from a tier that has security. This is needed
 * for deciding whether an org's projects need to be rescanned or not.
 * @param oldStripePlan - The name of the org's current Stripe plan.
 * @param newStripePlan - The name of the Stripe plan the org is changing to.
 * @returns False if neither plan name is the Compliance+Security plan.
 *          False if the org isn't changing tiers.
 *          True if the org is moving to or from the Compliance+Security tier.
 */
export const isSecurityPlanChange = (
  oldStripePlan: FossaPlanName,
  newStripePlan: FossaPlanName,
): boolean => {
  const isNewPlanSecurity = isSecurityPlan(newStripePlan);
  const isOldPlanSecurity = isSecurityPlan(oldStripePlan);
  if (
    (isNewPlanSecurity && isOldPlanSecurity) ||
    (!isNewPlanSecurity && !isOldPlanSecurity)
  ) {
    // Both security or neither are security
    return false;
  }

  // Check to see if the plan tier is staying the same or changing.
  return oldStripePlan !== newStripePlan;
};

export const toValidSeats = (val: number | string): number => {
  let intCount = Number(val);
  if (Number.isNaN(intCount)) {
    return MINIMUM_BUSINESS_SEATS;
  }

  if (intCount < MINIMUM_BUSINESS_SEATS) {
    intCount = MINIMUM_BUSINESS_SEATS;
  }

  while (intCount % MINIMUM_BUSINESS_SEATS_STEP !== 0) {
    intCount += 1;
  }

  if (intCount > MAXIMUM_BUSINESS_SEATS) {
    intCount = MAXIMUM_BUSINESS_SEATS;
  }

  return intCount;
};

export const hasUsedFreeTrial = (customer: Stripe.Customer): boolean => (customer.metadata?.used_free_trial === '1');
export const isUpdatingToFreeTrial = (
  customer: Stripe.Customer,
  currentPriceDetails: Stripe.Price,
  newPriceDetails: Stripe.Price,
): boolean => {
  // The organization has already used the free trial
  if (hasUsedFreeTrial(customer)) {
    return false;
  }
  // The free trial is only available when updating from FREE to BUSINESS
  const currentProduct = currentPriceDetails?.product as Stripe.Product;
  const newProduct = newPriceDetails?.product as Stripe.Product;
  const isFree = (currentProduct.metadata?.type === 'FREE' || currentPriceDetails.id === FREE_PLAN_NAME);

  return (isFree && newProduct.metadata?.type === 'BUSINESS');
};

export const isUpgrading = (
  customer: Stripe.Customer,
  currentPriceDetails: Stripe.Price,
  newPriceDetails: Stripe.Price,
): boolean => {
  if (!hasUsedFreeTrial(customer)) {
    return false;
  }

  const newProduct = newPriceDetails?.product as Stripe.Product;
  if (newProduct.metadata?.type === 'FREE') {
    return false;
  }

  return ((currentPriceDetails.unit_amount || 0) <= (newPriceDetails.unit_amount || 0));
};

export const isDowngrading = (
  customer: Stripe.Customer,
  currentPriceDetails: Stripe.Price,
  newPriceDetails: Stripe.Price,
): boolean => {
  if (!hasUsedFreeTrial(customer)) {
    return false;
  }

  const currentProduct = currentPriceDetails?.product as Stripe.Product;
  const newProduct = newPriceDetails?.product as Stripe.Product;

  if (currentProduct.metadata?.type !== 'BUSINESS' || newProduct.metadata?.type !== 'BUSINESS') {
    return false;
  }

  if ((currentPriceDetails.recurring?.interval === 'year') && (newPriceDetails.recurring?.interval === 'month')) {
    return true;
  }

  return ((currentPriceDetails.unit_amount || 0) > (newPriceDetails.unit_amount || 0));
};

export const isCancelling = (
  currentPriceDetails: Stripe.Price,
  newPriceDetails: Stripe.Price,
): boolean => {
  const currentProduct = currentPriceDetails?.product as Stripe.Product;
  const newProduct = newPriceDetails?.product as Stripe.Product;
  return (currentProduct.metadata?.type === 'BUSINESS' && newProduct.metadata?.type === 'FREE');
};
